/**
 * The monthly report on a Saidiseire site page: the CRM's side of the contract
 * and the rules the card needs (docs/saidiseire-kiirus-turve-raport.md, item D).
 *
 * A HAND-WRITTEN MIRROR of `apps/api/src/modules/crm-monitoring/monthly/types.ts`.
 * `monitoringMonthly.test.ts` reads that file and compares the field names of
 * every interface, and holds the send limits to the API's.
 *
 * React-free: which months there are to build, which addresses are ticked by
 * default, what a row's pill says and what the downloaded file is called are
 * decided here and tested without mounting anything.
 */
import { API_BASE, api } from "./api";
import { MONITOR_SITES_PATH } from "./monitoring";

// ── Contract (mirrors crm-monitoring/monthly/types.ts) ──────────────────────────

export type MonthReportStatus = "running" | "ready" | "failed";

export interface MonthReportRowView {
  month: string;
  partial: boolean;
  status: MonthReportStatus;
  from: string | null;
  to: string | null;
  generatedAt: string | null;
  sentAt: string | null;
  sentTo: string[];
  sentRedirectedFrom: string[];
  error: string | null;
}

export interface MonthlyRecipientView {
  email: string;
  name: string;
  primary: boolean;
}

export interface MonthlyCardResponse {
  allowed: boolean;
  reports: MonthReportRowView[];
  recipients: MonthlyRecipientView[];
  mailbox: boolean;
  currentMonth: string;
  firstMonth: string;
}

export interface MonthlyBuildResponse {
  report: MonthReportRowView;
}

export interface MonthlySendResponse {
  report: MonthReportRowView;
  /** Always "sent": a letter that did not leave comes back as the `send_failed` refusal. */
  transport: "sent";
  redirectedFrom: string[] | null;
}

/** The API's limits (`monthly/views.ts`), held equal by the mirror test. */
export const MONTHLY_SEND_TO_MAX = 5;
export const MONTHLY_NOTE_MAX = 2000;

// ── Routes ───────────────────────────────────────────────────────────────────

export const monthlyPath = (monitorId: string) => `${MONITOR_SITES_PATH}/${encodeURIComponent(monitorId)}/monthly`;

const monthPath = (monitorId: string, month: string) => `${monthlyPath(monitorId)}/${encodeURIComponent(month)}`;

export const getMonthly = (monitorId: string, signal?: AbortSignal): Promise<MonthlyCardResponse> =>
  api.get<MonthlyCardResponse>(monthlyPath(monitorId), signal);

export const buildMonthReport = (monitorId: string, month: string, force = false): Promise<MonthlyBuildResponse> =>
  api.post<MonthlyBuildResponse>(`${monthPath(monitorId, month)}/build`, force ? { force: true } : {});

export interface MonthlySendBody {
  to: string[];
  note?: string;
  resend?: boolean;
}

export const sendMonthReport = (monitorId: string, month: string, body: MonthlySendBody): Promise<MonthlySendResponse> =>
  api.post<MonthlySendResponse>(`${monthPath(monitorId, month)}/send`, body);

/** The file name from a `Content-Disposition` header, or the fallback. */
export function filenameOf(disposition: string | null, fallback: string): string {
  if (!disposition) return fallback;
  const star = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
  if (star) {
    try {
      return decodeURIComponent(star[1]!.trim());
    } catch {
      /* a malformed encoding: try the plain form */
    }
  }
  const plain = /filename="([^"]+)"/i.exec(disposition);
  return plain ? plain[1]! : fallback;
}

/**
 * Download a report's PDF: a fetch with the session cookie and a blob, not
 * `window.open`, the way the sales documents download (`sales.ts`).
 * Throws with the HTTP status when the server refuses.
 */
export async function downloadMonthReport(monitorId: string, month: string): Promise<void> {
  const res = await fetch(`${API_BASE}${monthPath(monitorId, month)}/pdf`, { credentials: "include" });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filenameOf(res.headers.get("Content-Disposition"), `raport-${month}.pdf`);
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// ── Rules ────────────────────────────────────────────────────────────────────

/** The month before a "YYYY-MM" key. */
export function previousMonthKey(month: string): string {
  const m = /^(\d{4})-(\d{2})$/.exec(month);
  if (!m) return month;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 2, 1));
  return d.toISOString().slice(0, 7);
}

/** "2026-09" as "September 2026" in the interface language, capitalised (it starts a row). */
export function monthLabel(month: string, locale: string): string {
  const m = /^(\d{4})-(\d{2})$/.exec(month);
  if (!m) return month;
  const tag = locale === "et" ? "et-EE" : "en-GB";
  const text = new Intl.DateTimeFormat(tag, { month: "long", year: "numeric", timeZone: "UTC" }).format(
    new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, 1)),
  );
  return text.charAt(0).toLocaleUpperCase(tag) + text.slice(1);
}

/** "2026-09-24" as "24.09.2026", the CRM's date format. */
export function dayLabel(day: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : day;
}

/**
 * The previous month when it can be built and has no report yet: the site
 * was watched in it, and the automatic build has not made one (a site added
 * this month, or a package that gained the report).
 */
export function missingPreviousMonth(card: Pick<MonthlyCardResponse, "currentMonth" | "firstMonth" | "reports">): string | null {
  const prev = previousMonthKey(card.currentMonth);
  if (prev < card.firstMonth) return null;
  return card.reports.some((r) => r.month === prev) ? null : prev;
}

/** The current month's interim report, if one exists. */
export function currentReport(card: Pick<MonthlyCardResponse, "currentMonth" | "reports">): MonthReportRowView | null {
  return card.reports.find((r) => r.month === card.currentMonth) ?? null;
}

/** A report is being built: the card asks again until it is done. */
export function anyRunning(card: Pick<MonthlyCardResponse, "reports"> | null): boolean {
  return Boolean(card?.reports.some((r) => r.status === "running"));
}

export type MonthlyRowState = "running" | "failed" | "sent" | "sentTest" | "ready";

/** What a row's pill says. */
export function rowState(row: MonthReportRowView): MonthlyRowState {
  if (row.status === "running") return "running";
  if (row.status === "failed") return "failed";
  if (row.sentAt) return row.sentRedirectedFrom.length > 0 ? "sentTest" : "sent";
  return "ready";
}

/** A report that was "sent" only to the test inbox: the client never got it. */
export function sentOnlyToTestInbox(row: Pick<MonthReportRowView, "sentAt" | "sentRedirectedFrom">): boolean {
  return row.sentAt !== null && row.sentRedirectedFrom.length > 0;
}

/**
 * A ready row's send button: the dark primary "send to the client" until the
 * report went out, then a plain "send again" (sending is no longer the row's
 * next step). A report that went only to the test inbox counts as sent here:
 * pressing the button again is a resend either way.
 */
export function sendButton(row: Pick<MonthReportRowView, "sentAt">): {
  tone: "dark" | "plain";
  key: "monitoring.monthly.sendAction" | "monitoring.monthly.sendAgain";
} {
  return row.sentAt ? { tone: "plain", key: "monitoring.monthly.sendAgain" } : { tone: "dark", key: "monitoring.monthly.sendAction" };
}

/**
 * The refusals that mean the card is behind the server (somebody else sent,
 * rebuilt or is building the report): the card reads the row again, so the
 * next press runs on its real state instead of meeting the same refusal.
 */
const BEHIND = new Set(["alreadySent", "reportBusy", "notReady", "reportNotFound"]);

export function refusalMeansCardIsBehind(errorKey: string): boolean {
  return BEHIND.has(errorKey);
}

export function rowTone(state: MonthlyRowState): "ok" | "wt" | "lt" | "info" | "mut" {
  if (state === "sent") return "ok";
  if (state === "sentTest") return "wt";
  if (state === "running") return "mut";
  if (state === "failed") return "lt";
  return "info";
}

/**
 * The addresses the send dialog ticks first: the client's own address and the
 * primary contacts, else the first address there is; never more than a send takes.
 */
export function defaultRecipients(recipients: readonly MonthlyRecipientView[]): string[] {
  const primary = recipients.filter((r) => r.primary).map((r) => r.email);
  const picked = primary.length ? primary : recipients.slice(0, 1).map((r) => r.email);
  return picked.slice(0, MONTHLY_SEND_TO_MAX);
}

/** Tick or untick one address, never past the limit. */
export function toggleRecipient(picked: readonly string[], email: string, on: boolean): string[] {
  if (!on) return picked.filter((e) => e !== email);
  if (picked.includes(email) || picked.length >= MONTHLY_SEND_TO_MAX) return [...picked];
  return [...picked, email];
}
